import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { get, post } from '../../utils/ajax';
import { remoteRoutes } from '../../data/constants';
import { isStudent } from '../../data/appRoles';
import Toast from '../../utils/Toast';

const CORAL = '#fe3a6a';
const ORANGE = '#fe8c45';

// Same four outcomes the admin form offers, plus one the admin form does
// not need: staff only ever record people who found work, but a student
// being asked every quarter must be able to say they have none. Without it
// a blocking dialog has no honest exit.
const PLACEMENT_TYPES = [
  { value: 'Employed', label: 'Employed' },
  { value: 'SelfEmployed', label: 'Self-Employed' },
  { value: 'Freelance', label: 'Freelancing' },
  { value: 'Internship', label: 'Internship' },
  { value: 'NotWorking', label: 'Not working at the moment' },
];

const GENDERS = ['Male', 'Female', 'Other'];
const INDUSTRIES = ['Tech', 'Design', 'Media', 'Other'];
const EMPLOYMENT_TYPES = [
  { value: 'FullTime', label: 'Full-time' },
  { value: 'PartTime', label: 'Part-time' },
  { value: 'Freelance', label: 'Freelance' },
  { value: 'Internship', label: 'Internship' },
];

const EARNING = ['Employed', 'SelfEmployed', 'Freelance'];

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Grid item xs={12}>
    <Divider style={{ margin: '4px 0 8px' }} />
    <Typography
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: 'var(--ev-textMuted)',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </Typography>
  </Grid>
);

/**
 * Asks a student what they are doing now, and will not let go until they
 * answer. Deliberately blocking — this is the only reliable moment to catch
 * employment outcomes, and an optional prompt gets ignored.
 *
 * Field-for-field the admin "Add Person" form, minus what we can look up
 * (name, course, hub) which is shown read-only instead of asked for.
 */
const EmploymentUpdateGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const user = useSelector((state: any) => state.core.user);
  const [checked, setChecked] = useState(false);
  const [due, setDue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [prefill, setPrefill] = useState<any>(null);
  const [form, setForm] = useState<any>({
    gender: '',
    phone: '',
    yearCompleted: '',
    placementType: '',
    jobTitle: '',
    salaryBeforeProgram: '',
    currentIncome: '',
    companyName: '',
    industry: '',
    employmentType: '',
    referredBy: '',
    internshipOrganization: '',
    internshipRole: '',
    internshipSupervisor: '',
    isPaidInternship: false,
    internshipStipend: '',
  });

  const student = isStudent(user);

  useEffect(() => {
    if (!student) {
      setChecked(true);
      return;
    }
    get(
      remoteRoutes.employmentMe,
      (resp: any) => {
        setDue(!!resp?.due);
        const pf = resp?.prefill;
        setPrefill(pf ?? null);
        if (pf) {
          const str = (v: any) =>
            v === null || v === undefined ? '' : String(v);
          setForm((f: any) => ({
            ...f,
            gender: pf.gender || '',
            phone: pf.phone || '',
            yearCompleted: str(pf.yearCompleted),
            placementType: pf.placementType || '',
            jobTitle: pf.jobTitle || '',
            salaryBeforeProgram: str(pf.salaryBeforeProgram),
            currentIncome: str(pf.currentIncome),
            companyName: pf.companyName || '',
            industry: pf.industry || '',
            employmentType: pf.employmentType || '',
            referredBy: pf.referredBy || '',
            internshipOrganization: pf.internshipOrganization || '',
            internshipRole: pf.internshipRole || '',
            internshipSupervisor: pf.internshipSupervisor || '',
            isPaidInternship: !!pf.isPaidInternship,
            internshipStipend: str(pf.internshipStipend),
          }));
        }
      },
      // Never let this prompt lock someone out because a request failed —
      // the app matters more than the survey.
      () => setDue(false),
      () => setChecked(true),
    );
  }, [student]);

  const set = (k: string) => (e: any) =>
    setForm({ ...form, [k]: e.target.value });
  const setNum = (k: string) => (e: any) =>
    setForm({ ...form, [k]: e.target.value.replace(/[^\d]/g, '') });

  const type = form.placementType;
  const earning = EARNING.includes(type);
  const employed = type === 'Employed';
  const internship = type === 'Internship';

  // Income is only asked for when the outcome implies earning, so it is
  // only required then. Zero is a legitimate answer; blank is not.
  const incomeMissing =
    earning && (form.salaryBeforeProgram === '' || form.currentIncome === '');

  const submit = () => {
    setAttempted(true);
    if (!type) {
      Toast.error('Please tell us what you are doing at the moment');
      return;
    }
    if (incomeMissing) {
      Toast.error('Please fill in both income amounts — enter 0 if none');
      return;
    }
    setSaving(true);
    post(
      remoteRoutes.employmentMe,
      { ...form },
      () => {
        Toast.success('Thank you — your details have been updated');
        setDue(false);
      },
      (err: any) =>
        Toast.error(
          err?.response?.body?.message || 'Could not save that, please retry',
        ),
      () => setSaving(false),
    );
  };

  if (!checked) return null;
  if (!due) return <>{children}</>;

  return (
    <>
      {children}
      <Dialog
        open
        maxWidth="sm"
        fullWidth
        scroll="paper"
        // No onClose, no escape, no backdrop dismissal — the whole point.
        disableBackdropClick
        disableEscapeKeyDown
        PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
      >
        <div
          style={{
            background: `linear-gradient(120deg, ${CORAL} 0%, ${ORANGE} 100%)`,
            color: '#fff',
            padding: '22px 26px',
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 800 }}>
            Quick update before you continue
          </div>
          <div
            style={{
              fontSize: 14,
              opacity: 0.93,
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            {prefill?.hasExistingRecord
              ? 'Check your details are still right and tell us what you are doing now.'
              : 'Tell us what you are doing now. This is how we show what the programme achieves.'}
          </div>
        </div>

        <DialogContent style={{ padding: '20px 26px 26px' }}>
          {/* What we already know — shown, not asked. */}
          {prefill && (
            <div
              style={{
                background: 'var(--ev-bgSubtle)',
                border: '1px solid var(--ev-border)',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 16,
                lineHeight: 1.5,
              }}
            >
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'var(--ev-textStrong)',
                }}
              >
                {prefill.fullName || 'Your details'}
              </div>
              {(prefill.courseName || prefill.hubName) && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: 'var(--ev-textMuted)',
                    marginTop: 2,
                  }}
                >
                  {[prefill.courseName, prefill.hubName]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
            </div>
          )}

          <Grid container spacing={2}>
            {/* Order mirrors the admin form: identity, then outcome, then
                the section that outcome opens up. */}
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label="Gender"
                value={form.gender}
                onChange={set('gender')}
              >
                {GENDERS.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Phone / WhatsApp"
                value={form.phone}
                inputProps={{ inputMode: 'numeric' }}
                onChange={setNum('phone')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Year completed"
                value={form.yearCompleted}
                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                onChange={setNum('yearCompleted')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label="Placement type"
                value={form.placementType}
                onChange={set('placementType')}
              >
                {PLACEMENT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {earning && (
              <>
                <SectionLabel>
                  {employed
                    ? 'Employment details'
                    : type === 'Freelance'
                    ? 'Freelance details'
                    : 'Self-employment details'}
                </SectionLabel>

                <Grid item xs={12} sm={employed ? 6 : 12}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={employed ? 'Job title' : 'Job title / what you do'}
                    value={form.jobTitle}
                    onChange={set('jobTitle')}
                  />
                </Grid>

                {employed && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Company name"
                        value={form.companyName}
                        onChange={set('companyName')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Industry"
                        value={form.industry}
                        onChange={set('industry')}
                      >
                        {INDUSTRIES.map((i) => (
                          <MenuItem key={i} value={i}>
                            {i}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Employment type"
                        value={form.employmentType}
                        onChange={set('employmentType')}
                      >
                        {EMPLOYMENT_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    required
                    label="Monthly income before"
                    value={form.salaryBeforeProgram}
                    inputProps={{ inputMode: 'numeric' }}
                    onChange={setNum('salaryBeforeProgram')}
                    error={attempted && form.salaryBeforeProgram === ''}
                    helperText={
                      attempted && form.salaryBeforeProgram === ''
                        ? 'Enter an amount, or 0'
                        : 'Enter 0 if you were not earning'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    required
                    label="Monthly income now"
                    value={form.currentIncome}
                    inputProps={{ inputMode: 'numeric' }}
                    onChange={setNum('currentIncome')}
                    error={attempted && form.currentIncome === ''}
                    helperText={
                      attempted && form.currentIncome === ''
                        ? 'Enter an amount, or 0'
                        : 'Enter 0 if you are not earning'
                    }
                  />
                </Grid>

                {employed && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Who referred you"
                      value={form.referredBy}
                      onChange={set('referredBy')}
                    />
                  </Grid>
                )}
              </>
            )}

            {internship && (
              <>
                <SectionLabel>Internship details</SectionLabel>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Where you're interning"
                    value={form.internshipOrganization}
                    onChange={set('internshipOrganization')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="What you do"
                    value={form.internshipRole}
                    onChange={set('internshipRole')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Who you work for"
                    value={form.internshipSupervisor}
                    onChange={set('internshipSupervisor')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        style={{ color: CORAL }}
                        checked={form.isPaidInternship}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            isPaidInternship: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <span style={{ fontSize: 13 }}>This internship pays</span>
                    }
                  />
                </Grid>
                {form.isPaidInternship && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Monthly stipend"
                      value={form.internshipStipend}
                      inputProps={{ inputMode: 'numeric' }}
                      onChange={setNum('internshipStipend')}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>

          <Button
            fullWidth
            variant="contained"
            disableElevation
            disabled={saving || !type || incomeMissing}
            onClick={submit}
            style={{
              marginTop: 22,
              background: type && !incomeMissing ? CORAL : 'var(--ev-bgSubtle)',
              color: type && !incomeMissing ? '#fff' : 'var(--ev-textMuted)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 8,
              padding: '10px 0',
            }}
          >
            {saving ? <CircularProgress size={20} /> : 'Submit and continue'}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmploymentUpdateGate;
